// src/components/timeBoxes/TaskSelector/useTaskSelector.ts
// 任務選擇器資料邏輯 Hook

import { useState, useMemo } from 'react';
import { Task, TaskCategory } from '../../../../types/task';
import { useRecentTasks } from '../../../../hooks/useRecentTasks';

interface UseTaskSelectorOptions {
  excludeTaskIds: string[];
  relatedTasks: Task[];
  userId: string;
}

/**
 * 任務選擇器資料邏輯
 *
 * 功能：
 * - 從 IndexedDB 載入最近任務
 * - 智能排序：優先顯示與已關聯任務相同分類的任務
 * - 即時搜尋過濾（本地）
 * - 預設顯示 10 筆
 */
export function useTaskSelector({ excludeTaskIds, relatedTasks, userId }: UseTaskSelectorOptions) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<TaskCategory | null>(null);

  // 從 IndexedDB 載入最近 100 筆任務（供智能排序與搜尋）
  const allTasks = useRecentTasks(userId, 100);

  // 分析已關聯任務的偏好分類
  const preferredCategories = useMemo(() => {
    const categoryCounts = new Map<TaskCategory, number>();

    relatedTasks.forEach(task => {
      if (task.category) {
        categoryCounts.set(task.category, (categoryCounts.get(task.category) || 0) + 1);
      }
    });

    if (categoryCounts.size === 0) return [];

    // 返回出現次數最多的分類（可能有多個）
    const maxCount = Math.max(...Array.from(categoryCounts.values()));
    return Array.from(categoryCounts.entries())
      .filter(([_, count]) => count === maxCount)
      .map(([category, _]) => category);
  }, [relatedTasks]);

  // 智能排序 + 過濾邏輯
  const filteredTasks = useMemo(() => {
    let tasks = allTasks.filter(task => !excludeTaskIds.includes(task.id));

    // 無搜尋時過濾已關閉任務（搜尋時允許找到已關閉任務，供用戶確認）
    if (!searchQuery.trim()) {
      tasks = tasks.filter(task => task.status !== 'CLOSED');
    }

    // 搜尋過濾（若有輸入）
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      tasks = tasks.filter(task =>
        task.title.toLowerCase().includes(query)
      );
    }

    // 分類過濾（若有選擇）
    if (selectedCategory) {
      tasks = tasks.filter(task => task.category === selectedCategory);
    }

    // 智能排序：偏好分類優先（僅在無搜尋時）
    if (!searchQuery.trim() && preferredCategories.length > 0) {
      tasks.sort((a, b) => {
        // 1. 偏好分類優先
        const aMatchesPreferred = a.category && preferredCategories.includes(a.category);
        const bMatchesPreferred = b.category && preferredCategories.includes(b.category);
        if (aMatchesPreferred && !bMatchesPreferred) return -1;
        if (!aMatchesPreferred && bMatchesPreferred) return 1;

        // 2. OPEN 狀態優先
        if (a.status === 'OPEN' && b.status !== 'OPEN') return -1;
        if (a.status !== 'OPEN' && b.status === 'OPEN') return 1;

        // 3. 最近更新時間
        const aTime = a.updatedAt?.getTime() || a.createdAt?.getTime() || 0;
        const bTime = b.updatedAt?.getTime() || b.createdAt?.getTime() || 0;
        return bTime - aTime;
      });
    } else if (searchQuery.trim()) {
      // 搜尋時按時間排序即可
      tasks.sort((a, b) => {
        const aTime = a.updatedAt?.getTime() || a.createdAt?.getTime() || 0;
        const bTime = b.updatedAt?.getTime() || b.createdAt?.getTime() || 0;
        return bTime - aTime;
      });
    }

    // 限制顯示 10 筆（保持簡潔）
    return tasks.slice(0, 10);
  }, [allTasks, excludeTaskIds, searchQuery, selectedCategory, preferredCategories]);

  return {
    filteredTasks,
    searchQuery,
    setSearchQuery,
    selectedCategory,
    setSelectedCategory,
    preferredCategories,
  };
}
